import React, { useEffect, useMemo, useRef } from 'react'
import { JWT_TOKEN_KEY, REFRESH_TOKEN_KEY, DEVICE_ID_KEY, USER_DATA_KEY } from '../../constants/auth'
import { Navigate, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../stores/auth'
import { clearTokenLocalStorage, getLocalStorage } from '../../utils/localStorage'
import { getSessionStorage } from '@/utils/sessionStorage'

type PrivateRouterProps = {
    Layout?: React.ComponentType<{ children?: React.ReactNode }> | null
    Element: React.ComponentType<any>
    allowedRoles?: string[]
}

const PrivateRouter: React.FC<PrivateRouterProps> = ({ Element, Layout, allowedRoles }) => {
    const navigate = useNavigate()
    const userAuth = useAuthStore((state) => state.user)
    const loginWithToken = useAuthStore((state) => state.loginWithToken)
    const loading = useAuthStore((state) => state.loading)

    const userSession = useMemo(() => getSessionStorage(USER_DATA_KEY), [])
    const jwt = useMemo(() => {
        return !!(getLocalStorage(JWT_TOKEN_KEY) && getLocalStorage(REFRESH_TOKEN_KEY) && getLocalStorage(DEVICE_ID_KEY))
    }, [])

    const alreadyCalled = useRef(false)

    useEffect(() => {
        if (loading) return;
        if (!userAuth && jwt) {
            alreadyCalled.current = true
            loginWithToken()
        }

        if (!userAuth && !jwt && !userSession) {
            clearTokenLocalStorage()
            navigate('/login')
        }
    }, [userAuth, jwt, loginWithToken, loading, navigate, userSession])

    if (loading) {
        return <div>Loading...</div>
    }

    if (!userAuth && !userSession && !jwt) {
        return <Navigate to="/login" />
    }

    // Check role-based access
    if (allowedRoles && allowedRoles.length > 0 && userAuth) {
        const userRole = userAuth.role?.toLowerCase()
        const hasAccess = allowedRoles.some(role => role.toLowerCase() === userRole)
        if (!hasAccess) {
            // If Manager tries to access unauthorized route, redirect to /s3-file
            if (userRole === 'manager') {
                return <Navigate to="/s3-file" replace />
            }
            // For other unauthorized access, redirect to home
            return <Navigate to="/" replace />
        }
    }

    return Layout ? (
        <Layout>
            <Element />
        </Layout>
    ) : (
        <Element />
    )
}

export default React.memo(PrivateRouter)